import styled from '@emotion/styled';
import { TRACKER_RENT_AND_BUY_BREAKS } from '@racemap/utilities/consts/billing';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { formatEuroPriceWithoutDigit } from '@racemap/utilities/functions/billing/formatEuroPriceWithoutDigit';
import { clamp } from '@racemap/utilities/math';
import { Col, InputNumber, Row, Slider, Typography } from 'antd';
import { useState } from 'react';
import { BillingLink } from '../BasicComponents/Links';

const { Text } = Typography;

export function CostCalculatorBuyTracker({ currentUserId }: { currentUserId?: string }) {
  const [devices, setDevices] = useState(10);
  const totalPricePro = getTrackerBuyPrice(devices, true);

  return (
    <Container>
      <Row>
        <Col sm={8} xs={20} />
        <Col sm={16} xs={20} className="table-col-price">
          GPS Tracker
        </Col>
      </Row>
      <Row style={{ marginBottom: '1rem' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          Number of devices
        </Col>
        <Col sm={16} xs={20} className="table-col-vat">
          Price level each device
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          1 to 4
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerBuyPriceSingle(1, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          5 to 19
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerBuyPriceSingle(5, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          20 to 39
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerBuyPriceSingle(20, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          40 to 99
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerBuyPriceSingle(40, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          100+
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(getTrackerBuyPriceSingle(100, true))}{' '}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ margin: '1.5rem 0' }}>
        <Col sm={8} xs={20} style={{ opacity: '0.7', fontSize: '15px' }}>
          Total price
        </Col>
        <Col sm={16} xs={20} className="table-col-price">
          {formatEuroPriceWithoutDigit(totalPricePro)}
          <div className="table-col-vat">&nbsp;+ VAT*</div>
        </Col>
      </Row>
      <hr />
      <Row style={{ marginTop: '3rem' }}>
        <Col>
          <Text strong style={{ opacity: 0.7, fontSize: '15px' }}>
            Number of devices
          </Text>
        </Col>
      </Row>
      <Row style={{ alignItems: 'center' }}>
        <Col span={16}>
          <Slider value={devices} min={1} max={200} onChange={setDevices} />
        </Col>
        <Col span={4} offset={4}>
          <InputNumber
            value={devices}
            min={1}
            max={200}
            step={1}
            onChange={(value) => {
              setDevices(value || 1);
            }}
          />
        </Col>
      </Row>
      {devices > 150 && (
        <Row>
          <Col sm={8} offset={8} style={{ display: 'flex', justifyContent: 'center' }}>
            <h3>
              <a
                style={{ color: RacemapColors.PaleBlue }}
                href={`mailto:info@racemap.com?subject=Request%20buy%20of%20${devices}%20GPS%20trackers`}
              >
                Contact Sales
              </a>
            </h3>
          </Col>
        </Row>
      )}
      {currentUserId != null && (
        <div style={{ marginTop: '1.5rem' }}>
          Find your current billing overview on your{' '}
          <BillingLink userId={currentUserId} label="billing page" />.
        </div>
      )}
    </Container>
  );
}

const Container = styled.div`
  .table-col-price {
    display: flex;
    justify-content: center;
    font-size: 20px;
    align-items: center;
  }

  .table-col-vat {
    opacity: 0.7;
    display: flex;
    justify-content: center;
    font-size: 15px;
    text-align: center;
  }

  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
  }
`;

function getTrackerBuyPrice(buyTracker: number, isPro: boolean) {
  const breaks = TRACKER_RENT_AND_BUY_BREAKS;
  return (
    clamp(0, buyTracker, breaks[0]) * getTrackerBuyPriceSingle(breaks[0], isPro) +
    clamp(0, buyTracker - breaks[0], breaks[1] - breaks[0]) *
      getTrackerBuyPriceSingle(breaks[0] + 1, isPro) +
    clamp(0, buyTracker - breaks[1], breaks[2] - breaks[1]) *
      getTrackerBuyPriceSingle(breaks[1] + 1, isPro) +
    clamp(0, buyTracker - breaks[2], breaks[3] - breaks[2]) *
      getTrackerBuyPriceSingle(breaks[2] + 1, isPro) +
    Math.max(buyTracker - breaks[3], 0) * getTrackerBuyPriceSingle(breaks[3] + 1, isPro)
  );
}

function getTrackerBuyPriceSingle(buyTracker: number, isPro: boolean) {
  const breaks = TRACKER_RENT_AND_BUY_BREAKS;
  if (buyTracker <= breaks[0]) {
    return isPro ? 15000 : 4500;
  }
  if (buyTracker <= breaks[1]) {
    return isPro ? 13500 : 4200;
  }
  if (buyTracker <= breaks[2]) {
    return isPro ? 12000 : 3900;
  }
  if (buyTracker <= breaks[3]) {
    return isPro ? 10500 : 3600;
  }
  return isPro ? 9000 : 3300;
}
